import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import parseAddress from '../utils/parseAddress';
import PageSkeleton from '../components/Skeleton';

const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#C0392B';

const SHIPPING_FEE = 12;

const GRADE_SHORT = {
  S: '未使用', A: '近新品', B: '美品', C: '可用',
};

export default function MarketCheckout() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // Address
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrForm, setAddrForm] = useState({ name: '', phone: '', province: '', city: '', district: '', detail: '' });
  const [editingAddrId, setEditingAddrId] = useState(null);
  const [pasteText, setPasteText] = useState('');

  // Wallet
  const [useWallet, setUseWallet] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [itemRes, addrRes] = await Promise.all([
          api.get(`/market/${id}`),
          api.get('/market/addresses').catch(() => ({ data: [] })),
        ]);
        setItem(itemRes.data);
        const addrs = addrRes.data || [];
        setAddresses(addrs);
        if (addrs.length > 0) {
          setSelectedAddr(addrs.find(a => a.is_default) || addrs[0]);
        }
        // Fetch wallet balance
        api.get('/deposits/balance').then(r => {
          setWalletBalance(Number(r.data?.available_cny || 0));
        }).catch(() => {});
      } catch {
        message.error('商品信息加载失败');
        navigate('/market');
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  if (loading) return <PageSkeleton />;
  if (!item) return null;

  const photos = item.photos?.length > 0
    ? item.photos
    : [`https://placehold.co/120x120/F5EDD8/B8892A?text=${encodeURIComponent(item.brand || '?')}`];
  const priceCny = Number(item.price_cny);
  const gradeText = GRADE_SHORT[item.item_grade] || item.item_grade || '';
  const walletDeduct = useWallet ? Math.min(walletBalance, priceCny + SHIPPING_FEE) : 0;
  const totalPrice = priceCny + SHIPPING_FEE - walletDeduct;

  const handleSaveAddress = async () => {
    const { name, phone, province, city, district, detail } = addrForm;
    if (!name || !phone || !detail) { message.warning('请填写完整地址信息'); return; }
    try {
      if (editingAddrId) {
        await api.put(`/market/addresses/${editingAddrId}`, addrForm);
      } else {
        await api.post('/market/addresses', { ...addrForm, is_default: addresses.length === 0 });
      }
      const res = await api.get('/market/addresses');
      const addrs = res.data || [];
      setAddresses(addrs);
      if (!selectedAddr || !editingAddrId) {
        setSelectedAddr(addrs[addrs.length - 1]);
      } else {
        setSelectedAddr(addrs.find(a => a.id === editingAddrId) || addrs[0]);
      }
      setAddrModalOpen(false);
      setAddrForm({ name: '', phone: '', province: '', city: '', district: '', detail: '' });
      setEditingAddrId(null);
      message.success(editingAddrId ? '地址已更新' : '地址已添加');
    } catch (e) {
      message.error(e.response?.data?.error || '保存失败');
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedAddr) { message.warning('请添加收件地址'); return; }
    setSubmitting(true);
    try {
      const addrText = `${selectedAddr.name} ${selectedAddr.phone} ${selectedAddr.province}${selectedAddr.city}${selectedAddr.district} ${selectedAddr.detail}`;
      const res = await api.post(`/market/${id}/buy`, {
        shipping_address: addrText,
        payment_method: useWallet ? 'wallet' : 'deposit',
      });
      message.success('下单成功！');
      navigate(`/market/orders/${res.data.id}`);
    } catch (e) {
      message.error(e.response?.data?.error || '下单失败');
    } finally { setSubmitting(false); }
  };

  const px = isMobile ? 16 : 48;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 140 : 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: `0 ${isMobile ? 0 : 24}px` }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK,
            padding: '0 8px 0 0',
          }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>确认订单</span>
          <div style={{ width: 24 }} />
        </div>

        {/* ── Shipping Address ── */}
        <div style={{ margin: `16px ${px}px 0` }}>
          {selectedAddr ? (
            <div onClick={() => setAddrModalOpen(true)} style={{
              background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, padding: '18px 16px',
              cursor: 'pointer', position: 'relative',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 15, color: INK }}>{selectedAddr.name}</span>
                <span style={{ fontSize: 14, color: MUTED }}>{selectedAddr.phone}</span>
              </div>
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
                {selectedAddr.province}{selectedAddr.city}{selectedAddr.district} {selectedAddr.detail}
              </div>
              <div style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                color: '#ccc', fontSize: 16,
              }}>›</div>
            </div>
          ) : (
            <div onClick={() => { setEditingAddrId(null); setAddrForm({ name: '', phone: '', province: '', city: '', district: '', detail: '' }); setAddrModalOpen(true); }} style={{
              background: '#fff', border: `2px dashed ${BD}`, borderRadius: 10, padding: '28px 16px',
              textAlign: 'center', cursor: 'pointer', color: TEAL, fontWeight: 500, fontSize: 15,
            }}>
              + 收件地址
            </div>
          )}
        </div>

        {/* ── Product Card ── */}
        <div style={{
          margin: `16px ${px}px 0`, background: '#fff', border: `1px solid ${BD}`,
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <img src={photos[0]} alt="" style={{
              width: 90, height: 90, borderRadius: 6, objectFit: 'cover', flexShrink: 0,
              background: '#F5EDD8',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 600, color: INK, lineHeight: 1.4,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {item.item_name}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {gradeText && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 11,
                    background: '#F5EDD8', color: MUTED,
                  }}>{gradeText}</span>
                )}
                {item.category && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 11,
                    background: '#F5EDD8', color: MUTED,
                  }}>{item.category}</span>
                )}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: INK, marginTop: 8, fontFamily: 'var(--mono)' }}>
                ¥{priceCny.toLocaleString()}
              </div>
            </div>
          </div>
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BD}40`,
            fontSize: 12, color: TEAL, lineHeight: 1.6,
          }}>
            正品保障 · 顺丰速运 · 寄卖商品不支持无理由退货
          </div>
        </div>

        {/* ── Delivery Info ── */}
        <div style={{
          margin: `16px ${px}px 0`, background: '#fff', border: `1px solid ${BD}`,
          borderRadius: 10, padding: '14px 16px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: MUTED }}>配送</span>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>顺丰速运</div>
              <div style={{ fontSize: 12, color: MUTED }}>今天18:00前发货，预计后天送达</div>
            </div>
          </div>
        </div>

        {/* ── Price Breakdown ── */}
        <div style={{
          margin: `16px ${px}px 0`, background: '#fff', border: `1px solid ${BD}`,
          borderRadius: 10, padding: '16px',
        }}>
          {[
            { label: '商品总额', value: `¥${priceCny.toFixed(2)}`, color: INK },
            { label: '运费', value: `¥${SHIPPING_FEE.toFixed(2)}`, color: INK, sub: true },
          ].map(row => (
            <div key={row.label} style={{
              display: 'flex', justifyContent: 'space-between', padding: '8px 0',
              borderBottom: `1px solid ${BD}30`, fontSize: 14,
            }}>
              <span style={{ color: MUTED, display: 'flex', alignItems: 'center', gap: 4 }}>
                {row.label}
                {row.sub && <span style={{ fontSize: 11, color: '#ccc' }}>?</span>}
              </span>
              <span style={{ color: row.color, fontFamily: 'var(--mono)' }}>{row.value}</span>
            </div>
          ))}

          {/* Wallet deduction */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BD}30`,
          }}>
            <span style={{ fontSize: 14, color: MUTED, display: 'flex', alignItems: 'center', gap: 4 }}>
              钱包抵扣 <span style={{ fontSize: 11, color: '#ccc' }}>?</span>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: MUTED }}>
                余额 ¥{walletBalance.toFixed(2)}
              </span>
              <div onClick={() => setUseWallet(!useWallet)} style={{
                width: 18, height: 18, borderRadius: '50%', cursor: 'pointer',
                border: `2px solid ${useWallet ? TEAL : '#ccc'}`,
                background: useWallet ? TEAL : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {useWallet && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>✓</span>}
              </div>
            </div>
          </div>

          {/* Total */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0 4px', marginTop: 4,
          }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: INK }}>合计</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: RED, fontFamily: 'var(--mono)' }}>
              ¥{totalPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* ── Buyer Notice ── */}
        <div style={{
          margin: `16px ${px}px 0`, background: '#FAFAF7', borderRadius: 10, padding: '16px',
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 8 }}>买家须知</div>
          <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.8 }}>
            鉴于二手商品特殊性，不支持7天无理由退货。提交订单即表示您已阅读并同意《云川中古交易协议》。
            如收到商品与描述严重不符，可在签收后48小时内联系客服申请退货退款。
            <br/><br/>
            1. 商品均为实物拍摄，因光线及屏幕差异可能存在轻微色差<br/>
            2. 品相等级由专业鉴定师评定，仅供参考<br/>
            3. 订单确认后30分钟内未付款将自动取消<br/>
            4. 发货后请注意查收，签收前请务必当面验货
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div style={{
        position: 'fixed', bottom: isMobile ? 56 : 0, left: 0, right: 0, background: '#fff',
        borderTop: `1px solid ${BD}`, padding: '10px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 101,
      }}>
        <div>
          <span style={{ fontSize: 13, color: MUTED }}>合计 </span>
          <span style={{ fontSize: 20, fontWeight: 700, color: RED, fontFamily: 'var(--mono)' }}>
            ¥ {totalPrice.toFixed(2)}
          </span>
        </div>
        <button onClick={handleSubmitOrder} disabled={submitting} style={{
          padding: '12px 40px', borderRadius: 24, border: 'none',
          background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
          boxShadow: '0 4px 16px rgba(26,107,84,.3)',
        }}>
          {submitting ? '提交中...' : '提交订单'}
        </button>
      </div>

      {/* ── Address Modal ── */}
      <Modal
        open={addrModalOpen}
        onCancel={() => setAddrModalOpen(false)}
        footer={null}
        title={null}
        width={420}
        centered
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600, fontSize: 16, color: INK, marginBottom: 16 }}>
            {addresses.length > 0 && !editingAddrId ? '选择收件地址' : (editingAddrId ? '编辑地址' : '添加收件地址')}
          </div>

          {/* Existing addresses list */}
          {addresses.length > 0 && !editingAddrId && (
            <div style={{ marginBottom: 16 }}>
              {addresses.map(addr => (
                <div key={addr.id} onClick={() => { setSelectedAddr(addr); setAddrModalOpen(false); }} style={{
                  padding: '12px 14px', borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                  border: `1px solid ${selectedAddr?.id === addr.id ? TEAL : BD}`,
                  background: selectedAddr?.id === addr.id ? `${TEAL}08` : '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14, color: INK }}>{addr.name}</span>
                      <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>{addr.phone}</span>
                    </div>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      setEditingAddrId(addr.id);
                      setAddrForm({
                        name: addr.name, phone: addr.phone,
                        province: addr.province || '', city: addr.city || '',
                        district: addr.district || '', detail: addr.detail || '',
                      });
                    }} style={{
                      background: 'none', border: 'none', color: TEAL, fontSize: 12, cursor: 'pointer',
                    }}>编辑</button>
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                    {addr.province}{addr.city}{addr.district} {addr.detail}
                  </div>
                </div>
              ))}
              <button onClick={() => {
                setEditingAddrId(null);
                setAddrForm({ name: '', phone: '', province: '', city: '', district: '', detail: '' });
              }} style={{
                width: '100%', padding: '10px 0', borderRadius: 6, border: `1px dashed ${BD}`,
                background: 'transparent', color: TEAL, fontSize: 13, cursor: 'pointer',
              }}>+ 添加新地址</button>
            </div>
          )}

          {/* Address form (show when no addresses or adding/editing) */}
          {(addresses.length === 0 || editingAddrId !== null || (addresses.length > 0 && editingAddrId === null && !addresses.length)) && (
            <>
              {(addresses.length === 0 || editingAddrId !== null) && (
                <div>
                  {/* Smart paste area */}
                  <div style={{
                    marginBottom: 16, padding: 14, borderRadius: 8,
                    background: `${TEAL}08`, border: `1px dashed ${TEAL}40`,
                  }}>
                    <div style={{ fontSize: 12, color: TEAL, fontWeight: 500, marginBottom: 6 }}>
                      智能粘贴
                    </div>
                    <textarea
                      value={pasteText}
                      onChange={e => setPasteText(e.target.value)}
                      placeholder="粘贴一段地址文字，自动识别姓名、手机号和地址&#10;例: 张三 13800138000 广东省深圳市南山区科技南路18号"
                      rows={2}
                      style={{
                        width: '100%', padding: '8px 10px', border: `1px solid ${BD}`, borderRadius: 6,
                        fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box',
                        background: '#fff',
                      }}
                    />
                    <button onClick={() => {
                      const parsed = parseAddress(pasteText);
                      if (!parsed) { message.warning('未能识别地址信息，请检查文字内容'); return; }
                      setAddrForm(prev => ({
                        name: parsed.name || prev.name,
                        phone: parsed.phone || prev.phone,
                        province: parsed.province || prev.province,
                        city: parsed.city || prev.city,
                        district: parsed.district || prev.district,
                        detail: parsed.detail || prev.detail,
                      }));
                      setPasteText('');
                      message.success('地址已自动填入');
                    }} disabled={!pasteText.trim()} style={{
                      marginTop: 8, padding: '6px 16px', borderRadius: 4, border: 'none',
                      background: pasteText.trim() ? TEAL : '#ccc', color: '#fff',
                      fontSize: 12, fontWeight: 500, cursor: pasteText.trim() ? 'pointer' : 'default',
                    }}>
                      识别并填入
                    </button>
                  </div>

                  {[
                    { key: 'name', label: '收件人', placeholder: '请输入收件人姓名' },
                    { key: 'phone', label: '手机号', placeholder: '请输入手机号' },
                    { key: 'province', label: '省', placeholder: '省份' },
                    { key: 'city', label: '市', placeholder: '城市' },
                    { key: 'district', label: '区', placeholder: '区/县' },
                    { key: 'detail', label: '详细地址', placeholder: '街道/门牌号/小区/楼栋' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>{f.label}</div>
                      <input
                        value={addrForm[f.key]}
                        onChange={e => setAddrForm({ ...addrForm, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        style={{
                          width: '100%', padding: '8px 12px', border: `1px solid ${BD}`, borderRadius: 6,
                          fontSize: 14, outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    {editingAddrId && (
                      <button onClick={() => { setEditingAddrId(null); }} style={{
                        flex: 1, padding: '10px 0', borderRadius: 6, border: `1px solid ${BD}`,
                        background: '#fff', color: MUTED, fontSize: 14, cursor: 'pointer',
                      }}>取消</button>
                    )}
                    <button onClick={handleSaveAddress} style={{
                      flex: 1, padding: '10px 0', borderRadius: 6, border: 'none',
                      background: TEAL, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>保存地址</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
